import { useEffect, useState } from "react";
import { Check, Mic, Type } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

function Frame({ title, text, children, testId }) {
  return (
    <section className="rounded-3xl border bg-white p-5 rise" data-testid={testId}>
      {title ? <h3 className="text-2xl leading-tight">{title}</h3> : null}
      {text ? <p className="mt-2 whitespace-pre-line text-base leading-relaxed text-[var(--ink-soft)]">{text}</p> : null}
      {children}
    </section>
  );
}

function DiscoveryWindow({ module, discovery, contributors }) {
  const item = discovery.find((d) => d.id === module.discovery_item_id);
  if (!item) return null;
  const contributor = contributors.find((c) => c.id === item.contributor_id);
  return (
    <Frame testId={`module-discovery-${module.key}`}>
      <p className="eyebrow">{(item.content_type || "").replace(/_/g, " ")}</p>
      <h3 className="mt-1 text-2xl">{item.title}</h3>
      {item.image ? (
        <img src={fileUrl(item.image)} alt="" className="mt-4 w-full rounded-2xl object-cover" />
      ) : null}
      {item.short_text ? <p className="mt-4 text-base leading-relaxed">{item.short_text}</p> : null}
      {item.long_text ? <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[var(--ink-soft)]">{item.long_text}</p> : null}
      {item.audio ? <audio controls src={fileUrl(item.audio)} className="mt-4 w-full" /> : null}
      {contributor ? (
        <p className="mt-4 text-xs text-[var(--ink-soft)]">Mitwirkend: {contributor.name}</p>
      ) : null}
      {item.source ? <p className="mt-1 text-xs text-[var(--ink-soft)]">Quelle: {item.source}</p> : null}
      {item.external_link ? (
        <a href={item.external_link} className="mt-2 inline-block text-xs underline" target="_blank" rel="noreferrer">
          mehr dazu
        </a>
      ) : null}
    </Frame>
  );
}

function Question({ module, value, onSave }) {
  return (
    <Frame title={module.title} text={module.text} testId={`module-question-${module.key}`}>
      <div className="mt-4 space-y-2">
        {(module.options || []).map((option) => (
          <button
            key={option.label}
            className={`w-full rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors ${
              value === option.label ? "bg-[var(--brand)] text-white" : "bg-white hover:bg-[var(--brand-soft)]"
            }`}
            onClick={() => onSave(option.label)}
            data-testid={`answer-${module.key}-${option.label.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
          >
            {option.label}
          </button>
        ))}
      </div>
      {value ? <p className="mt-3 text-xs text-[var(--ink-soft)]">Antwort gespeichert.</p> : null}
    </Frame>
  );
}

function PathChoice({ module, value, onChoose }) {
  return (
    <Frame title={module.title} text={module.text} testId={`module-path-${module.key}`}>
      <div className="mt-4 space-y-3">
        {(module.options || []).map((option) => (
          <button
            key={option.variant_key || option.label}
            className={`w-full rounded-2xl border px-4 py-4 text-left transition-colors ${
              value === option.variant_key ? "bg-[var(--brand)] text-white" : "bg-white hover:bg-[var(--brand-soft)]"
            }`}
            onClick={() => onChoose(option.variant_key)}
            data-testid={`path-${(option.variant_key || option.label).toLowerCase()}`}
          >
            <span className="block text-sm font-bold tracking-wide">{option.label.toUpperCase()}</span>
            {option.description ? <span className="mt-1 block text-sm opacity-80">{option.description}</span> : null}
          </button>
        ))}
      </div>
    </Frame>
  );
}

function CodeSearch({ module, walkId, stationId, found, total, onFound, isFundstueck, guestMode }) {
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reported, setReported] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const { data } = await api.post(`/progress/${walkId}/verify-code`, {
        station_id: stationId,
        module_key: module.key,
        code,
        found: guestMode ? found : undefined,
      });
      setOk(!!data.ok);
      setMessage(data.message);
      if (data.ok) {
        setCode("");
        onFound(data.found || [], data.total || 0);
      }
    } catch (e) {
      setOk(false);
      setMessage(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const sendReport = async () => {
    try {
      await api.post("/reports", { subject_type: "STATION", subject_id: stationId, message: reportText });
      setReported(true);
      setReporting(false);
    } catch (e) {
      setMessage(errMsg(e));
    }
  };

  const complete = total > 0 && found.length >= total;

  return (
    <Frame title={module.title} text={module.text} testId={`module-code-${module.key}`}>
      <p className="mt-4 text-3xl" data-testid={`code-progress-${module.key}`}>
        {found.length} / {total}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {(module.code_labels || []).map((label, index) => (
          <span
            key={label + index}
            className="rounded-full border px-3 py-1 text-xs"
            style={index < found.length ? { background: "var(--brand)", color: "#fff" } : {}}
          >
            {label || `Code ${index + 1}`}
          </span>
        ))}
      </div>
      {complete ? (
        <div className="mt-4 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm" data-testid={`code-complete-${module.key}`}>
          <p className="font-semibold">Gefunden.</p>
          {module.after_text ? <p className="mt-1">{module.after_text}</p> : null}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 flex gap-2">
          <input
            className="field"
            placeholder="Code eingeben"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid={`code-input-${module.key}`}
          />
          <button className="btn btn-primary shrink-0" disabled={busy} data-testid={`code-submit-${module.key}`}>
            PRÜFEN
          </button>
        </form>
      )}
      {message ? (
        <p className={`mt-2 text-sm ${ok ? "text-[var(--ink-soft)]" : "text-red-700"}`} data-testid={`code-message-${module.key}`}>
          {message}
        </p>
      ) : null}
      {isFundstueck ? (
        <div className="mt-4 border-t pt-4">
          {reported ? (
            <p className="text-xs text-[var(--ink-soft)]">Danke, die Gemeinde wurde informiert.</p>
          ) : reporting ? (
            <div className="space-y-2">
              <textarea
                className="field min-h-[80px]"
                placeholder="Was ist das Problem?"
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                data-testid="report-text"
              />
              <button className="btn btn-ghost !py-2 !text-xs" onClick={sendReport} data-testid="report-submit">
                PROBLEM SENDEN
              </button>
            </div>
          ) : (
            <button className="text-xs underline" onClick={() => setReporting(true)} data-testid="report-open">
              Problem melden
            </button>
          )}
        </div>
      ) : null}
    </Frame>
  );
}

function MessageChain({ module, stationId, onLeft }) {
  const [chain, setChain] = useState(null);
  const [mode, setMode] = useState("TEXT");
  const [text, setText] = useState("");
  const [audio, setAudio] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/chain/${stationId}`).then(({ data }) => setChain(data)).catch(() => setChain(null));
  }, [stationId]);

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/chain/${stationId}`, { text: mode === "TEXT" ? text : "", audio: mode === "AUDIO" ? audio : null });
      setChain((current) => ({ ...(current || {}), mine: data }));
      setText("");
      onLeft();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame title={module.title} text={module.text} testId={`module-chain-${module.key}`}>
      <div className="mt-4 rounded-2xl border bg-[var(--brand-soft)] p-4" data-testid="chain-previous">
        {chain?.previous ? (
          <>
            <p className="eyebrow">Von {chain.previous.author_name || "jemandem"}</p>
            {chain.previous.text ? <p className="mt-2 whitespace-pre-line text-base leading-relaxed">{chain.previous.text}</p> : null}
            {chain.previous.audio ? <audio controls src={fileUrl(chain.previous.audio)} className="mt-3 w-full" /> : null}
            {chain.previous.photo ? <img src={fileUrl(chain.previous.photo)} alt="" className="mt-3 w-full rounded-xl" /> : null}
          </>
        ) : (
          <p className="text-sm text-[var(--ink-soft)]">
            Noch hat niemand etwas hinterlassen. Du bist die erste Person.
          </p>
        )}
      </div>

      <div className="mt-5">
        <p className="eyebrow">Hinterlass dem nächsten Menschen etwas</p>
        <div className="mt-2 flex gap-2">
          <button
            className={`btn !py-2 !text-xs ${mode === "TEXT" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setMode("TEXT")}
            data-testid="chain-mode-text"
          >
            <Type className="h-4 w-4" /> TEXT
          </button>
          <button
            className={`btn !py-2 !text-xs ${mode === "AUDIO" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setMode("AUDIO")}
            data-testid="chain-mode-audio"
          >
            <Mic className="h-4 w-4" /> AUDIO
          </button>
        </div>
        {mode === "TEXT" ? (
          <textarea
            className="field mt-3 min-h-[110px]"
            placeholder="Deine Nachricht"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="chain-text"
          />
        ) : (
          <div className="mt-3">
            <ImageUpload value={audio} onChange={setAudio} label="Sprachnachricht (Audiodatei)" testId="chain-audio" accept="audio/*" />
          </div>
        )}
        {error ? <p className="mt-2 text-sm text-red-700" data-testid="chain-error">{error}</p> : null}
        <button className="btn btn-primary mt-3 w-full" onClick={submit} disabled={busy} data-testid="chain-submit">
          {chain?.mine ? "NACHRICHT ERSETZEN" : "NACHRICHT HINTERLASSEN"}
        </button>
        {chain?.mine ? (
          <p className="mt-2 text-xs text-[var(--ink-soft)]" data-testid="chain-mine">
            Deine aktive Nachricht: {chain.mine.text || "Sprachnachricht"}
          </p>
        ) : null}
      </div>
    </Frame>
  );
}

function PhotoModule({ module, value, onChange }) {
  return (
    <Frame title={module.title} text={module.text} testId={`module-photo-${module.key}`}>
      <div className="mt-4">
        <ImageUpload value={value} onChange={onChange} label="Genau ein Foto" testId={`photo-${module.key}`} />
      </div>
    </Frame>
  );
}

export function StationModule(props) {
  const { module } = props;
  switch (module.type) {
    case "ENTDECKUNGSFENSTER":
      return <DiscoveryWindow {...props} />;
    case "COMMUNITY_FRAGE":
      return <Question {...props} value={props.answer} onSave={props.onAnswer} />;
    case "WEGWAHL":
      return <PathChoice {...props} value={props.pathChoice} onChoose={props.onChoosePath} />;
    case "CODE_SUCHE":
      return <CodeSearch {...props} isFundstueck={false} guestMode={props.guestMode} />;
    case "FUNDSTUECK":
      return <CodeSearch {...props} isFundstueck guestMode={props.guestMode} />;
    case "NACHRICHT_KETTE":
      return <MessageChain {...props} />;
    case "TEXT_ODER_AUDIO":
      return null;
    case "FOTO":
      return <PhotoModule {...props} value={props.photo} onChange={props.onPhoto} />;
    case "ACHIEVEMENT_PROGRESS":
      return (
        <Frame testId={`module-progress-${module.key}`}>
          <p className="eyebrow">Fortschritt</p>
          <p className="mt-1 text-3xl" data-testid="fragment-progress">{props.fragmentLabel}</p>
        </Frame>
      );
    default:
      return <Frame title={module.title} text={module.text} testId={`module-info-${module.key}`} />;
  }
}

export { Frame };
export default StationModule;
